import React, { useState, useCallback, useEffect } from 'react';
import { SpinnerGap } from '@phosphor-icons/react';
import { submitPrincipleFeedback, getPrincipleLibraryConfig } from '@/lib/api';
import { INTENT_TO_BUTTON } from '@/config/task-display-config';
import {
    DS_BORDERS,
    DS_COLORS,
    DS_SPACING,
    DS_TYPOGRAPHY,
} from '@/config/ui-invariants';
import { toast } from 'sonner';

/**
 * PrinciplePromotionDetail — Inbox detail renderer for work_type=principle_promotion.
 *
 * WHAT THIS CARD IS FOR. The deal screen has been watching a rule ride along on real
 * decks without letting it change any grade. When it has ridden enough of them, this
 * card asks the CEO one question: should this rule start holding a grade down?
 *
 * WHY IT READS THE WAY IT DOES (WI-TD-PLAIN-CARDS-AND-THE-IDLE-SCREEN-QUESTION-2026-08-13).
 * The previous face was written in the machinery's vocabulary — "observe → capping",
 * "Decks ridden", "Overrode", "Lens utility", "Calibration Reading" — four counters in
 * boxes and a button labelled "Promote to capping". Every one of those is a word the CEO
 * has to translate before he can decide, and the standing rule is that no internal
 * vocabulary ever reaches him. The freeze suite already enforces exactly this standard on
 * the hold's own wording (backend/tests/test_promotion_freeze.py — "internal vocabulary
 * leaked to the CEO"); this card is now held to the same bar, string for string, by
 * backend/tests/test_principle_promotion_card_plain_language.py.
 *
 * So the face is now, in order: what the rule says, the deck that taught it, its record
 * in words, and what each of the two choices will actually do. The raw counters still
 * exist — demoted into a closed "exact numbers" drawer, for when he wants them.
 *
 * COPY LIVES IN CONFIG, NOT HERE. Every CEO-facing string is served by
 * GET /api/principle-library/config → review_ui.promotion_card. COPY_FALLBACK below is
 * the local mirror used only until that response lands (the same idiom as
 * PrincipleLibraryAgent.jsx, whose tab labels carry the identical note). Config always
 * wins. Both copies are held to the same vocabulary bar by the test above — a fallback
 * that leaks jargon is the leak, whether or not config would have overwritten it.
 *
 * Payload shape (from item.payload), all fields optional and rendered only when present:
 *   linked_principle_id  — ID passed to POST /api/principle-library/{id}/feedback
 *   title                — short form of the rule
 *   principle_text       — the rule in full
 *   trigger_condition    — when the rule applies
 *   falsifying_condition — what a deck must show to escape it
 *   worked_example       — { deck_ref, before_band, after_band, rationale }
 *   authored_at          — when a grandfathered rule was written, if recorded
 *   decks_ridden / kept_count / overrode_count / override_rate — the raw counters
 *   held_by_freeze       — true while the CEO's promotion freeze stands
 *   freeze_explanation   — the freeze's OWN plain words (promotion_freeze.explain)
 *
 * The last two are forwarded from the card's doc root by work_inbox.py's
 * PRINCIPLE_PROMOTION bypass; before that they were written and never read, so a held
 * card reached the inbox unable to say why it was holding.
 *
 * Authority: WI-PRINCIPLE-LIBRARY-GOVERNANCE-BUILD-001; face rebuilt by
 * WI-TD-PLAIN-CARDS-AND-THE-IDLE-SCREEN-QUESTION-2026-08-13.
 */

// Fallback copy — overridden by review_ui.promotion_card once /config loads. Fallbacks
// only. Held to the same no-internal-vocabulary bar as the config block itself.
const COPY_FALLBACK = {
    intro: 'A rule the deal screen has been watching is ready for your decision.',
    heading_rule: 'What this rule says',
    heading_why: 'The judgment it makes',
    heading_how: 'How it would apply',
    heading_story: 'The deck that taught it',
    heading_record: 'Its record so far',
    // Mirrors review_ui.promotion_card.move_labels key-for-key. A move with no entry here
    // and none in config renders NOTHING — never its slug.
    move_labels: {
        competitive_moat_structural_vs_temporary: 'Whether an advantage is structural, or just a head start.',
        check_cohort_concentration: 'Whether an average is hiding who is actually buying.',
        verify_distribution_claim: 'Whether the company owns its route to customers, or only reaches them.',
        assess_founder_insight_vs_recitation: 'Whether the founder knows something, or is repeating what the market already says.',
        evaluate_unit_economics_path: 'Whether the economics can reach profit from where they stand.',
        check_evidence_grade: 'Whether the evidence is strong enough for the claim resting on it.',
        surface_structural_contradiction: 'Whether two parts of the plan pull against each other.',
        interrogate_metric_survivability: 'Whether a number would survive the thing that usually kills it.',
        habit_structure_vs_one_off: 'Whether the buying is a habit, or a one-off.',
        assess_willingness_to_pay: 'Whether anyone has shown they will actually pay for this.',
        probe_why_now: 'Whether anything has changed that makes this possible now.',
        causal_attribution_vs_correlation: 'Whether the growth was caused by what the company believes caused it.',
    },
    how_when: 'When a deck does this',
    how_unless: 'and cannot show',
    how_caps_then: "this rule holds that deck's grade down to caution.",
    how_flags_only_then: 'this rule says so on the screen. It does not change the grade — it is still in trial, and only a rule you have promoted can hold a grade down.',
    how_scope_all_sectors: 'It applies to decks in every sector.',
    how_scope_named_sectors: 'It applies only to decks in: {sectors}.',
    how_ceiling_limit: 'It can only ever hold a grade down, never raise one — and it changes nothing for a deck already graded at caution or below.',
    heading_numbers: 'The exact numbers',
    numbers_screens: 'Real screens it appeared on',
    numbers_kept: 'Times you kept its read',
    numbers_overruled: 'Times you overruled it',
    numbers_agreement: 'How often you agreed',
    story_deck: 'The deck',
    story_before: 'Graded at first',
    story_after: 'Graded after this rule',
    story_why: 'Why it changed',
    story_absent_dated: 'No worked story yet — you wrote this rule and approved it on {date}.',
    story_absent_undated: 'No worked story yet — you wrote this rule and approved it, though the date it was approved is not on record.',
    record_screens_none: 'It has not appeared on a real screen yet.',
    record_screens_one: 'It has appeared on 1 real screen.',
    record_screens_many: 'It has appeared on {count} real screens.',
    record_never_overruled: 'You have never overruled it.',
    record_overruled_one: 'You have overruled it once.',
    record_overruled_many: 'You have overruled it {count} times.',
    promote_label: 'Let this rule set a floor',
    // promote_when / promote_unless removed 2026-08-14 — the same two facts now lead the
    // card in how_when / how_unless. See the note of the same name in config.
    promote_then: 'it cannot be graded above caution.',
    promote_plain_fallback: 'From now on this rule stops only watching and starts holding a deck’s grade down to caution when it applies.',
    park_label: 'Set it aside',
    park_explains: 'The rule goes quiet and stops appearing here. Nothing is deleted, and you can bring it back whenever you want.',
    held_heading: 'On hold — by your own decision',
    held_no_action: 'Nothing is needed from you yet. This rule keeps watching real decks and building its record while it waits.',
    error_no_id: 'This rule could not be identified, so nothing was saved. Nothing has changed.',
    error_save_failed: 'That did not save. Nothing has changed — please try again.',
    success_promoted: 'Done. This rule now sets a floor on the grade.',
    success_parked: 'Done. This rule has gone quiet.',
};

// (The card's own `status` literal used to be read here as a second hold signal. It is
// deliberately no longer consulted — see the note on `isHeld` below — so the constant that
// held it has gone with it rather than being left behind as a name nothing reads.)

// Fill {name} placeholders. A template missing its placeholder is returned unchanged
// rather than silently blanked.
function fill(template, values) {
    if (!template) return '';
    return Object.keys(values || {}).reduce(
        (out, k) => out.split(`{${k}}`).join(String(values[k])),
        template,
    );
}

/**
 * A readable date, or null when the value is absent or unparseable.
 *
 * Returning null is load-bearing: a grandfathered rule with no worked example renders a
 * sentence that names the date it was approved, and if that date is not on record the
 * card says so. It never invents one and never prints a raw timestamp.
 */
function readableDate(value) {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export function PrinciplePromotionDetail({ item, onRefresh }) {
    const [busy, setBusy] = useState(null);
    const [copy, setCopy] = useState(COPY_FALLBACK);

    useEffect(() => {
        let alive = true;
        getPrincipleLibraryConfig()
            .then((cfg) => {
                if (alive && cfg && cfg.promotion_card && Object.keys(cfg.promotion_card).length) {
                    setCopy({ ...COPY_FALLBACK, ...cfg.promotion_card });
                }
            })
            .catch(() => { /* fallback copy already in state — the card still reads correctly */ });
        return () => { alive = false; };
    }, []);

    const payload = item?.payload || {};
    const principleId = payload.linked_principle_id || item?.linked_principle_id;
    const title = payload.title || item?.title || '';
    const ruleText = payload.principle_text || title;
    const trigger = (payload.trigger_condition || '').trim();
    const falsifier = (payload.falsifying_condition || '').trim();
    const worked = payload.worked_example || null;
    const authoredOn = readableDate(payload.authored_at);

    // The judgment this rule encodes. `analytical_move` is a controlled-vocabulary SLUG;
    // it is looked up, never printed. No label -> the WHY block does not render, which is
    // the correct failure: a missing label is a copy gap, not a licence to show him
    // "competitive_moat_structural_vs_temporary".
    const moveLabel = (copy.move_labels || {})[payload.analytical_move] || null;

    // Whether this rule can hold a grade down TODAY. Derived server-side into a boolean so
    // no mode string ever crosses the wire (work_inbox.py's PRINCIPLE_PROMOTION bypass).
    const setsFloorToday = Boolean(payload.sets_a_floor_today);

    // Which decks it reaches. EMPTY IS MEANINGFUL: every lens in the library is recorded
    // universal (empty safe_sector_classes), so [] means "every sector" and must not be
    // read as "not recorded". Absent (null/undefined) is the unknown case and falls back to
    // the same sentence, because a rule with no recorded restriction is unrestricted — the
    // library's own seed says so.
    const sectorClasses = payload.safe_sector_classes || {};
    const namedSectors = [
        ...(sectorClasses.sector_l1 || []),
        ...(sectorClasses.sector_l2 || []),
    ].filter(Boolean);
    const sectorSentence = namedSectors.length
        ? fill(copy.how_scope_named_sectors, { sectors: namedSectors.join(', ') })
        : copy.how_scope_all_sectors;

    const decksRidden = payload.decks_ridden ?? 0;
    const keptCount = payload.kept_count ?? 0;
    const overrodeCount = payload.overrode_count ?? 0;
    const total = keptCount + overrodeCount;
    const keepRate = total > 0 ? Math.round((keptCount / total) * 100) : null;

    // Held while the CEO's own promotion freeze stands.
    //
    // DERIVED SERVER-SIDE, AND THE CARD'S OWN STATUS IS NOT CONSULTED
    // (WI-PROMOTER-FOLD-CAPPING-WIRING-AND-CARD-2026-08-14). This read
    //     Boolean(payload.held_by_freeze) || item?.status === 'held_by_ceo_freeze'
    // and its reasoning ("so a card written before the flag existed still reads as held")
    // are kept here verbatim because they were correct when written and the drift must not
    // be silently overwritten. What changed: `status` is deliberately PERMANENT — nothing
    // re-stamps a held card, by design (work_inbox.py::_held_promotion_inbox_clauses says
    // so in its own words). So the `||` clause could never go false. A card returned to the
    // inbox by the lifting of the freeze would still have rendered as held, with no action
    // bar — the CEO would have got his three cards back and been unable to touch any of
    // them, which is D-2026-08-12-27 defeated at the last inch.
    //
    // `held_by_freeze` is now computed fresh per request from promotion_freeze.is_frozen()
    // and is the ONLY signal. It fails closed (an unreadable declaration reads as held), so
    // dropping the status fallback removes a stuck `true`, never a safety net.
    const isHeld = Boolean(payload.held_by_freeze);
    const freezeWords = payload.freeze_explanation || null;

    const handleFeedback = useCallback(async (action) => {
        if (!principleId) {
            toast.error(copy.error_no_id);
            return;
        }
        setBusy(action);
        try {
            await submitPrincipleFeedback(principleId, action);
            toast.success(action === 'promote_to_capping' ? copy.success_promoted : copy.success_parked);
            onRefresh && onRefresh();
        } catch (e) {
            toast.error(copy.error_save_failed);
        } finally {
            setBusy(null);
        }
    }, [principleId, onRefresh, copy]);

    // The record, in words. Sentences rather than counters — the drawer below still
    // carries the raw numbers for anyone who wants them.
    const screensSentence = decksRidden === 0
        ? copy.record_screens_none
        : decksRidden === 1
            ? copy.record_screens_one
            : fill(copy.record_screens_many, { count: decksRidden });
    const overruledSentence = overrodeCount === 0
        ? copy.record_never_overruled
        : overrodeCount === 1
            ? copy.record_overruled_one
            : fill(copy.record_overruled_many, { count: overrodeCount });

    return (
        <div style={{ fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily }}>
            <h2
                style={{
                    fontFamily: DS_TYPOGRAPHY.listItemTitle.fontFamily,
                    fontSize: DS_TYPOGRAPHY.listItemTitle.fontSize,
                    fontWeight: DS_TYPOGRAPHY.listItemTitle.fontWeight,
                    color: DS_COLORS.text.primary,
                    marginBottom: '4px',
                    marginTop: 0,
                }}
            >
                {title}
            </h2>

            {/* The hold, in the freeze's own words, on the card's face. */}
            {isHeld ? (
                <Notice tone="hold" heading={copy.held_heading}>
                    {freezeWords ? `${freezeWords} ` : ''}{copy.held_no_action}
                </Notice>
            ) : (
                <Notice tone="ready">{copy.intro}</Notice>
            )}

            <Section heading={copy.heading_rule}>
                <Prose>{ruleText}</Prose>
            </Section>

            {/* WHY — the judgment this rule encodes, in investment terms.
                Rendered ONLY when the move has a label in copy.move_labels. An unlabelled
                move renders nothing at all rather than its slug: analytical_move is
                controlled machine vocabulary and D-2026-08-12-23 forbids it reaching him. */}
            {moveLabel ? (
                <Section heading={copy.heading_why}>
                    <Prose>{moveLabel}</Prose>
                </Section>
            ) : null}

            {/* HOW IT WOULD APPLY — reach and teeth, stated before the record rather than
                beside the buttons.

                RENDERED WHILE HELD TOO, deliberately. This block used to live only inside
                the `!isHeld` branch at the bottom, so a card the CEO could not yet action
                was also a card that could not tell him what actioning it would do — which
                is the half of the decision he is actually being asked to make. It offers no
                action, so showing it does not advertise one. */}
            <Section heading={copy.heading_how}>
                {trigger && falsifier ? (
                    <>
                        <Prose>{copy.how_when}:</Prose>
                        <Quote>{trigger}</Quote>
                        <Prose>{copy.how_unless}:</Prose>
                        <Quote>{falsifier}</Quote>
                        <Prose>{setsFloorToday ? copy.how_caps_then : copy.how_flags_only_then}</Prose>
                    </>
                ) : (
                    <Prose>{setsFloorToday ? copy.promote_plain_fallback : copy.how_flags_only_then}</Prose>
                )}
                <Prose>{sectorSentence}</Prose>
                <Prose>{copy.how_ceiling_limit}</Prose>
            </Section>

            <Section heading={copy.heading_story}>
                {worked && (worked.deck_ref || worked.rationale) ? (
                    <div className="flex flex-col gap-3">
                        {worked.deck_ref ? <Line label={copy.story_deck} value={worked.deck_ref} /> : null}
                        {worked.before_band ? <Line label={copy.story_before} value={worked.before_band} /> : null}
                        {worked.after_band ? <Line label={copy.story_after} value={worked.after_band} /> : null}
                        {worked.rationale ? <Line label={copy.story_why} value={worked.rationale} /> : null}
                    </div>
                ) : (
                    /* Grandfathered rule: the CEO wrote it himself before worked examples
                       were required. The date is named when it is on record and its absence
                       is stated when it is not — never a manufactured one. */
                    <Prose>
                        {authoredOn
                            ? fill(copy.story_absent_dated, { date: authoredOn })
                            : copy.story_absent_undated}
                    </Prose>
                )}
            </Section>

            <Section heading={copy.heading_record}>
                <Prose>{screensSentence} {overruledSentence}</Prose>
            </Section>

            {/* The raw counters, demoted. Closed by default; the card reads without them. */}
            <details style={{ marginBottom: DS_SPACING.paragraphSpacing + 'px' }}>
                <summary
                    className="cursor-pointer"
                    style={{
                        fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                        fontSize: '14px',
                        fontWeight: 700,
                        color: DS_COLORS.text.secondary,
                        minHeight: DS_SPACING.minTouchTarget + 'px',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {copy.heading_numbers}
                </summary>
                <div className="flex gap-4 flex-wrap mt-2">
                    <StatBox label={copy.numbers_screens} value={String(decksRidden)} />
                    <StatBox label={copy.numbers_kept} value={String(keptCount)} />
                    <StatBox label={copy.numbers_overruled} value={String(overrodeCount)} />
                    {keepRate !== null && (
                        <StatBox label={copy.numbers_agreement} value={`${keepRate}%`} />
                    )}
                </div>
            </details>

            {/* What each choice does. This sits directly above the action bar rather than
                inside it: DESIGN_LANGUAGE_SKILL.md Section 4 fixes the action-bar layout
                (secondary middle, spacer, primary right) and that layout is not ours to
                rearrange — so the explanation is adjacent to the bar and names each button
                verbatim, which is what the CEO needs and what the layout law allows. */}
            {!isHeld && (
                <div className="flex flex-col gap-4 mb-2">
                    {/* The when/unless/then trio used to be repeated here verbatim. It now
                        leads the card in "How it would apply" above, so this states the
                        CONSEQUENCE of the click only — the same words twice on one screen is
                        the duplicate-truth shape, and the second copy is the one that goes
                        stale. */}
                    <Choice buttonLabel={copy.promote_label}>
                        <Prose>{copy.promote_then}</Prose>
                    </Choice>
                    <Choice buttonLabel={copy.park_label}>
                        <Prose>{copy.park_explains}</Prose>
                    </Choice>
                </div>
            )}

            {/* Action bar. Secondary left of the spacer, primary alone on the right —
                DESIGN_LANGUAGE_SKILL.md Section 4.

                NOT RENDERED AT ALL while the hold stands (WI-TD-CLEAR-THE-DEAD-CARDS-AND-
                FIX-THE-SURFACE-2026-08-12). It previously rendered both buttons `disabled`,
                which is what the CEO saw and reported as "the buttons are not clickable".
                A disabled button is still an OFFER — it says the system will take this
                decision from you and is merely busy. The freeze means the opposite: the
                decision is not available at all, by his own standing ruling. So the card
                shows no action rather than a refused one, and the hold band above states
                who held it and what lifts it. This does not weaken the freeze — the freeze
                already refuses these calls server-side; it stops the surface advertising
                an action the server will decline. */}
            {!isHeld && (
            <div
                className="flex items-center gap-3 mt-6 pt-4 flex-wrap"
                style={{ borderTop: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}` }}
            >
                <button
                    onClick={() => handleFeedback('park')}
                    disabled={!!busy}
                    className={`inline-flex items-center gap-2 px-4 rounded-md border ${INTENT_TO_BUTTON.neutral.bg} ${INTENT_TO_BUTTON.neutral.text} ${INTENT_TO_BUTTON.neutral.border} ${INTENT_TO_BUTTON.neutral.hover} disabled:opacity-50`}
                    style={{
                        minHeight: DS_SPACING.minTouchTarget + 'px',
                        borderRadius: DS_BORDERS.radiusButton,
                        fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                        fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                        fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                    }}
                >
                    {busy === 'park' ? <SpinnerGap className="w-4 h-4 animate-spin" /> : null}
                    {copy.park_label}
                </button>
                <div className="flex-1" />
                <button
                    onClick={() => handleFeedback('promote_to_capping')}
                    disabled={!!busy}
                    className={`inline-flex items-center gap-2 px-5 rounded-md border ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border} ${INTENT_TO_BUTTON.primary.hover} disabled:opacity-50`}
                    style={{
                        minHeight: DS_SPACING.minTouchTarget + 'px',
                        borderRadius: DS_BORDERS.radiusButton,
                        fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                        fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                        fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                    }}
                >
                    {busy === 'promote_to_capping' ? <SpinnerGap className="w-4 h-4 animate-spin" /> : null}
                    {copy.promote_label}
                </button>
            </div>
            )}
        </div>
    );
}

// ─── building blocks ───────────────────────────────────────────────────────────

function Section({ heading, children }) {
    return (
        <div style={{ marginBottom: DS_SPACING.paragraphSpacing + 'px' }}>
            <div
                style={{
                    fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                    fontSize: DS_TYPOGRAPHY.sectionLabel.fontSize,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: DS_COLORS.text.secondary,
                    marginBottom: '6px',
                }}
            >
                {heading}
            </div>
            {children}
        </div>
    );
}

function Prose({ children }) {
    return (
        <p
            style={{
                fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
                color: DS_COLORS.text.primary,
                margin: '0 0 6px 0',
                whiteSpace: 'pre-wrap',
            }}
        >
            {children}
        </p>
    );
}

// A condition quoted verbatim from the rule. Indented behind a brand rule so it reads as
// the rule's own words rather than as the card's.
function Quote({ children }) {
    return (
        <p
            style={{
                fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
                color: DS_COLORS.text.primary,
                borderLeft: `3px solid ${DS_COLORS.border.brand}`,
                paddingLeft: '12px',
                margin: '0 0 10px 0',
            }}
        >
            {children}
        </p>
    );
}

function Line({ label, value }) {
    return (
        <div>
            <div
                style={{
                    fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                    fontSize: '13px',
                    fontWeight: 700,
                    color: DS_COLORS.text.secondary,
                    marginBottom: '2px',
                }}
            >
                {label}
            </div>
            <div
                style={{
                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                    fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                    lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
                    color: DS_COLORS.text.primary,
                }}
            >
                {value}
            </div>
        </div>
    );
}

// A notice band.
//
// BOTH tones sit on the SAME tinted surface (surface.warm, #F0F0F8) rather than on a
// tone-matched fill, for two reasons. First, there is no amber surface token — DS_COLORS
// carries signal.moderate as a LINE colour only, and inventing a background hex here
// would be a raw-hex violation (Section 7 rule 1). Second, a band whose fill matches its
// container is invisible by construction (P-2034), and this card renders on white; the
// tint is what makes the band a band. The tone is carried by the 4px left accent, which
// is exactly where DESIGN_LANGUAGE_SKILL.md Section 3 puts a signal colour. Text stays
// text.primary on the tint (#111827 on #F0F0F8) — well clear of the contrast floor, and
// never a grey-on-grey.
function Notice({ tone, heading, children }) {
    const isHold = tone === 'hold';
    const accent = isHold ? DS_COLORS.signal.moderate : DS_COLORS.border.brand;
    return (
        <div
            className="mb-5 mt-3 p-3 rounded"
            style={{
                background: DS_COLORS.surface.warm,
                border: `${DS_BORDERS.width} solid ${accent}`,
                borderLeft: `4px solid ${accent}`,
            }}
        >
            {heading ? (
                <p
                    style={{
                        fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                        fontSize: '14px',
                        fontWeight: 700,
                        color: DS_COLORS.text.primary,
                        margin: '0 0 4px 0',
                    }}
                >
                    {heading}
                </p>
            ) : null}
            <p
                style={{
                    fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                    fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                    lineHeight: DS_TYPOGRAPHY.bodyMd.lineHeight,
                    color: DS_COLORS.text.primary,
                    margin: 0,
                    fontWeight: isHold ? 400 : 600,
                }}
            >
                {children}
            </p>
        </div>
    );
}

// One of the two choices, explained. The button's own label is repeated verbatim as the
// heading so the explanation is unambiguously attached to the button it describes.
//
// FILL, NOT JUST A BORDER. This renders inside InboxPreviewRouter's pane, whose background
// is `DS_COLORS.surface.white` (InboxPreviewRouter.jsx:136 — read, not assumed). A white box
// on a white pane is separated only by a hairline #E5E7EB border, which is the P-2034 shape
// exactly: a block whose fill equals its container's is invisible by construction, and that
// card was raised for a CEO who is visually impaired. `surface.slate` differs from the pane
// by fill AND keeps the border; text stays text.primary on it (#111827 on #F8F9FA, ~16.8:1).
function Choice({ buttonLabel, children }) {
    return (
        <div
            className="p-3 rounded"
            style={{
                background: DS_COLORS.surface.slate,
                border: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}`,
            }}
        >
            <div
                style={{
                    fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                    fontSize: '15px',
                    fontWeight: 700,
                    color: DS_COLORS.text.primary,
                    marginBottom: '6px',
                }}
            >
                {buttonLabel}
            </div>
            {children}
        </div>
    );
}

function StatBox({ label, value }) {
    return (
        <div
            className="flex flex-col items-center justify-center rounded-lg border px-5 py-3"
            style={{
                // Same reason as Choice above: the pane behind this is white, so a white
                // fill would leave only a hairline border to separate the box from it.
                minWidth: '80px',
                background: DS_COLORS.surface.slate,
                borderColor: DS_COLORS.border.default,
            }}
        >
            <span
                style={{
                    fontFamily: DS_TYPOGRAPHY.kpiValue.fontFamily,
                    fontSize: '22px',
                    fontWeight: 600,
                    color: DS_COLORS.text.primary,
                    lineHeight: 1.2,
                }}
            >
                {value}
            </span>
            <span
                style={{
                    fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                    // 13px, matching Line's label. The face this replaced set these at
                    // 11px uppercase with 0.08em tracking — the smallest text on the card,
                    // for a reader who is visually impaired. Raised, and the uppercasing
                    // dropped, because these labels are now sentences-worth of meaning
                    // ("Times you overruled it") rather than one-word column heads.
                    fontSize: '13px',
                    fontWeight: 700,
                    color: DS_COLORS.text.secondary,
                    marginTop: '2px',
                    textAlign: 'center',
                }}
            >
                {label}
            </span>
        </div>
    );
}
